import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ═══════════════════════════════════════════════════════════════
// FIXED GEOMETRY — constant across ALL themes
// ═══════════════════════════════════════════════════════════════
const GEO = {
  flapHeight: '38%',
  cardHeight: '64%',
  cardInsetX: '7%',
  cardBottom: '12%',
  envelopeRadius: 28,
  cardRadius: 24,
};

// ═══════════════════════════════════════════════════════════════
// REFINED MOTION CONFIG
// Phase timeline (ms from tap):
//   0–100     tap feedback (press scale)
//   100–220   flap tension (small resist)
//   220–760   flap release/open
//   760–900   flap settle
//   380–1150  card reveal phase 1 (slow peek)
//   520–1400  invitation bg starts appearing
//   700–1550  invitation text starts appearing
//   1150–1450 card reveal phase 2 (full rise)
//   1450–1550 card settle
//   1100–1750 envelope fades out (dissolve, not drop)
// ═══════════════════════════════════════════════════════════════
const M = {
  ease: [0.22, 1, 0.36, 1],

  // Flap: resist → release → settle
  flap: {
    duration: 0.95,
    angles: [0, -20, -160, -165],      // tension at -20, release to -160, settle at -165
    times: [0, 0.15, 0.8, 1],
    y: [0, -1, -5, -6],
    shadows: [
      'drop-shadow(0 2px 8px rgba(0,0,0,0.10))',
      'drop-shadow(0 4px 12px rgba(0,0,0,0.12))',
      'drop-shadow(0 12px 28px rgba(0,0,0,0.18))',
      'drop-shadow(0 8px 18px rgba(0,0,0,0.14))',
    ],
  },

  // Card: peek → rise → settle
  card: {
    duration: 1.15,
    delay: 0.28,
    y: [0, -62, -115, -110],           // emerge → reveal → slight rest
    scale: [0.96, 0.99, 1, 1],
    rotate: [0, 0.4, 0.1, 0],
    times: [0, 0.7, 0.9, 1],           // long peek, short rise, tiny settle
    shadows: [
      'drop-shadow(0 8px 20px rgba(0,0,0,0.10))',
      'drop-shadow(0 14px 28px rgba(0,0,0,0.13))',
      'drop-shadow(0 18px 36px rgba(0,0,0,0.16))',
      'drop-shadow(0 16px 30px rgba(0,0,0,0.14))',
    ],
  },

  seal: { duration: 0.35, delay: 0.05 },

  // Envelope exit: dissolve (not drop)
  fade: { delay: 1.1, duration: 0.65 },

  // Invitation world: starts appearing DURING envelope motion
  bg: { delay: 0.15, duration: 1.2 },

  // Total sequence
  total: 1900,
};

// ═══════════════════════════════════════════════════════════════
// THEME DEFINITIONS
// ═══════════════════════════════════════════════════════════════
const ENVELOPE_THEMES = {
  'ivory-classic': {
    name: 'Ivory Classic',
    colors: {
      envelope: '#f5f0e8', inner: '#e8dfd3', flap: '#ede6da', flapBack: '#d9cfc1',
      card: '#fffdf9', seal: '#c4956a', accent: '#d4af37', border: '#d9cfc1',
      shadow: 'rgba(139,119,91,0.25)', sealIcon: 'monogram',
      bg: 'radial-gradient(ellipse at center, #faf6f0 0%, #ede6da 100%)',
    },
    swatches: ['#f5f0e8', '#c4956a', '#d4af37'],
    assets: {},
  },
  'ivory-letterpress': {
    name: 'Ivory Letterpress',
    colors: {
      envelope: '#EFE6DC', inner: '#E5D8C7', flap: '#E8DDD0', flapBack: '#D9CCBB',
      card: '#FFFFFF', seal: '#722F37', accent: '#C6A86B', border: '#E5D8C7',
      shadow: 'rgba(120,100,75,0.3)', sealIcon: 'monogram',
      bg: 'radial-gradient(ellipse at center, #F7F3EE 0%, #EFE6DC 100%)',
    },
    swatches: ['#EFE6DC', '#722F37', '#C6A86B'],
    assets: {
      base: '/themes/ivory-letterpress/envelope-base.png',
      flap: '/themes/ivory-letterpress/flap.png',
      innerCard: '/themes/ivory-letterpress/inner-card.png',
      seal: '/themes/ivory-letterpress/seal.png',
      revealBg: '/themes/ivory-letterpress/reveal-bg.png',
    },
  },
  'blue-floral': {
    name: 'Blue Floral',
    colors: {
      envelope: '#e8eef5', inner: '#d3dde8', flap: '#dce5ed', flapBack: '#c1cfdb',
      card: '#f9fafd', seal: '#6b8eb5', accent: '#7ba1c7', border: '#b5c7d9',
      shadow: 'rgba(80,100,139,0.25)', sealIcon: 'monogram',
      bg: 'radial-gradient(ellipse at center, #f0f4f8 0%, #dce5ed 100%)',
    },
    swatches: ['#e8eef5', '#6b8eb5', '#7ba1c7'],
    assets: {
      base: '/themes/blue-floral/envelope-base.png',
      flap: '/themes/blue-floral/flap.png',
      innerCard: '/themes/blue-floral/inner-card.png',
      overlay: '/themes/blue-floral/front-overlay.png',
      seal: '/themes/blue-floral/seal.png',
      revealBg: '/themes/blue-floral/reveal-bg.png',
    },
  },
  'sage-garden': {
    name: 'Sage Garden',
    colors: {
      envelope: '#e8ede6', inner: '#d3ddd0', flap: '#dce5d8', flapBack: '#c1cfbb',
      card: '#fafdf9', seal: '#7a9b6d', accent: '#7a9b6d', border: '#b5c7ad',
      shadow: 'rgba(91,119,80,0.25)', sealIcon: 'leaf',
      bg: 'radial-gradient(ellipse at center, #f2f5f0 0%, #dce5d8 100%)',
    },
    swatches: ['#e8ede6', '#7a9b6d', '#7a9b6d'],
    assets: {},
  },
  'dusty-rose': {
    name: 'Dusty Rose',
    colors: {
      envelope: '#f0e4e4', inner: '#e0d0d0', flap: '#ecdcdc', flapBack: '#d4bfbf',
      card: '#fdf9f9', seal: '#c4858a', accent: '#c4858a', border: '#d4bfbf',
      shadow: 'rgba(140,100,105,0.25)', sealIcon: 'heart',
      bg: 'radial-gradient(ellipse at center, #f5f0f0 0%, #ecdcdc 100%)',
    },
    swatches: ['#f0e4e4', '#c4858a', '#c4858a'],
    assets: {},
  },
  'midnight-gold': {
    name: 'Midnight Gold',
    colors: {
      envelope: '#2a2a35', inner: '#1e1e28', flap: '#333340', flapBack: '#1a1a25',
      card: '#faf8f4', seal: '#d4af37', accent: '#d4af37', border: '#444455',
      shadow: 'rgba(0,0,0,0.4)', sealIcon: 'monogram',
      bg: 'radial-gradient(ellipse at center, #1a1a25 0%, #0d0d15 100%)',
    },
    swatches: ['#2a2a35', '#d4af37', '#d4af37'],
    assets: {},
  },
};

// ═══════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════

// ─── Wax Seal (CSS fallback) ────────────────────────────────
function CssSeal({ color, icon, initials = '&', size = 52 }) {
  return (
    <div
      className="rounded-full flex items-center justify-center"
      style={{
        width: size, height: size,
        background: `radial-gradient(circle at 35% 35%, ${color}dd, ${color})`,
        boxShadow: `0 2px 8px rgba(0,0,0,0.3), inset 0 -2px 4px rgba(0,0,0,0.15), inset 0 2px 4px rgba(255,255,255,0.15)`,
      }}
    >
      {icon === 'heart' ? (
        <svg width={size * 0.45} height={size * 0.45} viewBox="0 0 24 24" fill="rgba(255,255,255,0.85)">
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
        </svg>
      ) : icon === 'leaf' ? (
        <svg width={size * 0.45} height={size * 0.45} viewBox="0 0 24 24" fill="rgba(255,255,255,0.85)">
          <path d="M17 8C8 10 5.9 16.17 3.82 21.34l1.89.66.95-2.3c.48.17.98.3 1.34.3C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75" />
        </svg>
      ) : (
        <span className="font-serif italic" style={{
          fontSize: size * 0.38, color: 'rgba(255,255,255,0.9)',
          textShadow: '0 1px 2px rgba(0,0,0,0.15)', letterSpacing: '0.05em',
        }}>
          {initials}
        </span>
      )}
    </div>
  );
}

// ─── Envelope Base ──────────────────────────────────────────
function EnvelopeBase({ theme }) {
  const c = theme.colors;
  if (theme.assets?.base) {
    return (
      <div className="absolute inset-0 overflow-hidden" style={{
        borderRadius: GEO.envelopeRadius,
        boxShadow: `0 20px 60px ${c.shadow}, 0 4px 12px ${c.shadow}`,
      }}>
        <img src={theme.assets.base} alt="" className="absolute inset-0 h-full w-full object-cover" />
        {theme.assets.overlay && (
          <img src={theme.assets.overlay} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover" />
        )}
        {/* Inner shadow — cavity illusion */}
        <div className="absolute inset-0 pointer-events-none" style={{
          boxShadow: 'inset 0 12px 30px rgba(0,0,0,0.18)',
          borderRadius: GEO.envelopeRadius,
        }} />
      </div>
    );
  }
  return (
    <div
      className="absolute inset-0 overflow-hidden"
      style={{
        borderRadius: GEO.envelopeRadius,
        background: c.envelope,
        border: `1px solid ${c.border}`,
        boxShadow: `0 20px 60px ${c.shadow}, 0 4px 12px ${c.shadow}`,
      }}
    >
      <div className="absolute bottom-0 left-0 right-0" style={{
        height: '45%', background: c.inner, clipPath: 'polygon(0 100%, 100% 100%, 50% 15%)',
      }} />
      <div className="absolute top-0 left-0 bottom-0" style={{
        width: '50%', background: c.inner, opacity: 0.5, clipPath: 'polygon(0 0, 0 100%, 50% 50%)',
      }} />
      <div className="absolute top-0 right-0 bottom-0" style={{
        width: '50%', background: c.inner, opacity: 0.5, clipPath: 'polygon(100% 0, 100% 100%, 50% 50%)',
      }} />
      {/* Inner shadow — cavity illusion (CSS fallback themes) */}
      <div className="absolute inset-0 pointer-events-none" style={{
        boxShadow: 'inset 0 12px 30px rgba(0,0,0,0.18)',
        borderRadius: GEO.envelopeRadius,
      }} />
    </div>
  );
}

// ─── Envelope Flap — resist → release → settle ─────────────
function EnvelopeFlap({ theme, isOpening, isOpened }) {
  const c = theme.colors;
  const open = isOpening || isOpened;

  return (
    <motion.div
      className="absolute inset-x-0 top-0 origin-top"
      style={{
        height: GEO.flapHeight,
        transformStyle: 'preserve-3d',
        zIndex: open ? 5 : 40,
      }}
      initial={false}
      animate={open ? {
        rotateX: M.flap.angles,
        y: M.flap.y,
        filter: M.flap.shadows,
      } : {
        rotateX: 0, y: 0,
        filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.08))',
      }}
      transition={{ duration: M.flap.duration, ease: M.ease, times: M.flap.times }}
    >
      {theme.assets?.flap ? (
        <div className="absolute inset-0" style={{
          backfaceVisibility: 'hidden',
          clipPath: 'polygon(0 0, 100% 0, 50% 100%)',
          borderRadius: `${GEO.envelopeRadius}px ${GEO.envelopeRadius}px 0 0`,
          overflow: 'hidden',
        }}>
          <img src={theme.assets.flap} alt="" className="h-full w-full object-cover object-center" />
          {/* Underside shading — flap has thickness */}
          <div className="absolute inset-0 pointer-events-none" style={{
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.18), rgba(0,0,0,0))',
            opacity: open ? 0.6 : 0.2,
            transition: 'opacity 0.4s',
          }} />
        </div>
      ) : (
        <>
          <div className="absolute inset-0" style={{
            background: c.flap,
            clipPath: 'polygon(0 0, 100% 0, 50% 100%)',
            border: `1px solid ${c.border}`,
            backfaceVisibility: 'hidden',
            borderRadius: `${GEO.envelopeRadius}px ${GEO.envelopeRadius}px 0 0`,
          }}>
            {/* Underside shading — CSS fallback */}
            <div className="absolute inset-0 pointer-events-none" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.15), rgba(0,0,0,0))',
              opacity: open ? 0.6 : 0.15,
              transition: 'opacity 0.4s',
            }} />
          </div>
          <div className="absolute inset-0" style={{
            background: c.flapBack,
            clipPath: 'polygon(0 0, 100% 0, 50% 100%)',
            transform: 'rotateX(180deg)',
            backfaceVisibility: 'hidden',
          }} />
        </>
      )}
    </motion.div>
  );
}

// ─── Inner Card — peek → rise → settle ──────────────────────
function InnerCard({ theme, isOpening, isOpened, coupleNames, eventDate, accent }) {
  const c = theme.colors;
  const open = isOpening || isOpened;

  return (
    <motion.div
      className="absolute z-20 overflow-hidden"
      style={{
        left: GEO.cardInsetX, right: GEO.cardInsetX,
        bottom: GEO.cardBottom, height: GEO.cardHeight,
        borderRadius: GEO.cardRadius,
      }}
      initial={false}
      animate={open ? {
        y: M.card.y,
        scale: M.card.scale,
        rotate: M.card.rotate,
        opacity: [0.96, 1, 1, 1],
        filter: M.card.shadows,
      } : {
        y: 0, scale: 0.96, rotate: 0, opacity: 0.96,
        filter: 'drop-shadow(0 8px 20px rgba(0,0,0,0.10))',
      }}
      transition={{
        duration: M.card.duration, ease: M.ease,
        delay: open ? M.card.delay : 0,
        times: M.card.times,
      }}
    >
      {theme.assets?.innerCard ? (
        <div className="relative h-full w-full">
          <img src={theme.assets.innerCard} alt="" className="h-full w-full object-cover" />
          {/* Light sweep — paper catching light */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={isOpening ? { opacity: [0, 0.4, 0] } : { opacity: 0 }}
            transition={{ duration: 1.2, delay: 0.4 }}
            style={{ background: 'linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.6), transparent 70%)' }}
          />
        </div>
      ) : (
        <div className="h-full w-full flex flex-col items-center justify-center text-center px-6 py-4 relative"
          style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: GEO.cardRadius }}
        >
          <div className="text-xs uppercase tracking-[0.3em] mb-3 opacity-60" style={{ color: accent }}>
            You're Invited
          </div>
          <div className="font-serif text-xl sm:text-2xl leading-tight mb-2" style={{ color: '#333' }}>
            {coupleNames || 'Together with joy'}
          </div>
          {eventDate && (
            <div className="text-xs tracking-wider mt-3 opacity-50">{eventDate}</div>
          )}
          <div className="mx-auto mt-4" style={{ width: 40, height: 1, background: accent, opacity: 0.4 }} />
          {/* Light sweep — CSS fallback card */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={isOpening ? { opacity: [0, 0.35, 0] } : { opacity: 0 }}
            transition={{ duration: 1.2, delay: 0.4 }}
            style={{
              background: 'linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.5), transparent 70%)',
              borderRadius: GEO.cardRadius,
            }}
          />
        </div>
      )}
    </motion.div>
  );
}

// ─── Seal (breaks on open with slight delay) ────────────────
function Seal({ theme, isOpening, isOpened, initials, contained }) {
  const c = theme.colors;
  const open = isOpening || isOpened;
  const size = contained ? 40 : 52;

  return (
    <motion.div
      className="absolute z-[45]"
      style={{ top: '37.5%', left: '50%', transform: 'translateX(-50%)' }}
      initial={false}
      animate={open ? { opacity: 0, scale: 0.6, y: -15 } : { opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: M.seal.duration, delay: open ? M.seal.delay : 0 }}
    >
      {theme.assets?.seal ? (
        <img src={theme.assets.seal} alt="" style={{ width: size, height: size }} className="object-contain" />
      ) : (
        <CssSeal color={c.seal} icon={c.sealIcon} initials={initials} size={size} />
      )}
    </motion.div>
  );
}

// ─── "Tap to open" hint ─────────────────────────────────────
function OpenHint({ onOpen, isDark }) {
  return (
    <motion.button
      type="button"
      onClick={onOpen}
      className="absolute inset-x-0 -bottom-14 z-50 mx-auto w-fit rounded-full px-5 py-2.5 text-sm shadow-md backdrop-blur-sm"
      style={{
        background: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.85)',
        color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.5)',
        border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
      }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.35 }}
      data-testid="envelope-tap-hint"
    >
      Tap to open
    </motion.button>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
//
// Phase flow:  closed → opening → opened → done
// "opening" = flap/card animations active
// "opened"  = envelope fading out, invitation visible underneath
// "done"    = envelope removed from DOM
//
// Key callbacks:
//   onOpening() → fires when animation starts (for parent overlap)
//   onComplete() → fires when sequence ends
// ═══════════════════════════════════════════════════════════════
export default function EnvelopeAnimation({
  onComplete,
  onOpening,
  coupleNames = '',
  eventDate = '',
  theme: themeId = 'ivory-classic',
  customAssets = null,
  autoOpen = false,
  contained = false,
}) {
  const [phase, setPhase] = useState('closed');
  const [pressed, setPressed] = useState(false);
  const [skipAnim, setSkipAnim] = useState(false);
  const timerRef = useRef(null);

  const baseTheme = ENVELOPE_THEMES[themeId] || ENVELOPE_THEMES['ivory-classic'];
  // Merge custom assets over theme defaults
  const theme = useMemo(() => {
    if (!customAssets || Object.keys(customAssets).length === 0) return baseTheme;
    return { ...baseTheme, assets: { ...baseTheme.assets, ...customAssets } };
  }, [baseTheme, customAssets]);
  const c = theme.colors;
  const isDark = themeId === 'midnight-gold';
  const hasRevealBg = !!(theme.assets?.revealBg);

  const prefersReducedMotion = useMemo(() =>
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
  , []);

  // Session storage: skip on return visit
  useEffect(() => {
    if (contained) return;
    const key = `envelope_seen_${window.location.pathname}`;
    if (sessionStorage.getItem(key)) {
      setSkipAnim(true);
      onComplete?.();
    }
  }, [contained, onComplete]);

  // Auto-open
  useEffect(() => {
    if (autoOpen && phase === 'closed' && !skipAnim) {
      timerRef.current = setTimeout(() => handleOpen(), 1500);
      return () => clearTimeout(timerRef.current);
    }
  }, [autoOpen, phase, skipAnim]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpen = useCallback(() => {
    if (phase !== 'closed') return;

    if (!contained) {
      const key = `envelope_seen_${window.location.pathname}`;
      sessionStorage.setItem(key, 'true');
    }

    if (prefersReducedMotion) {
      setPhase('done');
      onComplete?.();
      return;
    }

    // Phase: opening — flap/card animate, invitation bg starts appearing
    setPhase('opening');
    onOpening?.();

    // Phase: opened — triggers envelope dissolve (fade delay in M.fade.delay
    // handles the timing from this moment, so we set 'opened' quickly)
    // This lets the fade.delay=1.1s count from TAP, not from a delayed phase change
    requestAnimationFrame(() => setPhase('opened'));

    // Phase: done — envelope removed from DOM
    setTimeout(() => {
      setPhase('done');
      onComplete?.();
    }, M.total);
  }, [phase, onComplete, onOpening, prefersReducedMotion, contained]);

  const handleSkip = useCallback(() => {
    if (!contained) {
      const key = `envelope_seen_${window.location.pathname}`;
      sessionStorage.setItem(key, 'true');
    }
    setPhase('done');
    onComplete?.();
  }, [onComplete, contained]);

  // Tap press feedback
  const handlePressStart = useCallback(() => setPressed(true), []);
  const handlePressEnd = useCallback(() => setPressed(false), []);

  if (skipAnim) return null;

  const initials = coupleNames
    ? coupleNames.split(/\s*[&+]\s*/).map(n => (n.trim()[0] || '')).join('&')
    : '&';

  const isOpening = phase === 'opening';
  const isOpened = phase === 'opened';
  const isClosed = phase === 'closed';
  const open = isOpening || isOpened;

  const envelopeWidth = contained ? 'min(75%, 260px)' : undefined; // full-screen uses className

  return (
    <AnimatePresence>
      {phase !== 'done' && (
        <motion.div
          className={`${contained ? 'absolute' : 'fixed'} inset-0 z-[9999] flex items-center justify-center overflow-hidden ${contained ? '' : 'px-4 pt-6 pb-10'}`}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          data-testid="envelope-animation"
        >
          {/* ─── Background Layer ──────────────────────────────── */}
          {/* Fades out with the envelope to reveal invitation underneath */}
          <motion.div
            className="absolute inset-0"
            initial={{ opacity: 1 }}
            animate={isOpened ? { opacity: 0 } : { opacity: 1 }}
            transition={isOpened ? {
              delay: M.fade.delay - 0.15,
              duration: M.fade.duration + 0.2,
              ease: M.ease,
            } : { duration: 0.3 }}
          >
            {hasRevealBg ? (
              <>
                <div className="absolute inset-0" style={{ background: c.bg }} />
                <motion.div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${theme.assets.revealBg})` }}
                  initial={{ opacity: 0, scale: 1.04 }}
                  animate={open
                    ? { opacity: 1, scale: 1 }
                    : { opacity: 0, scale: 1.04 }
                  }
                  transition={{ delay: M.bg.delay, duration: M.bg.duration, ease: M.ease }}
                />
              </>
            ) : (
              <div className="absolute inset-0" style={{ background: c.bg }} />
            )}
          </motion.div>

          {/* Subtle atmosphere shift */}
          <motion.div
            className="absolute inset-0"
            initial={false}
            animate={open
              ? { backgroundColor: 'rgba(255,255,255,0.04)' }
              : { backgroundColor: isDark ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.15)' }
            }
            transition={{ duration: 0.8, ease: M.ease }}
          />

          {/* ─── Skip Button ──────────────────────────────────── */}
          {!contained && (
            <button
              onClick={handleSkip}
              className="absolute top-6 right-6 text-sm opacity-40 hover:opacity-80 transition-opacity z-50"
              style={{ color: isDark ? '#888' : '#999' }}
              data-testid="envelope-skip-btn"
            >
              Skip
            </button>
          )}

          {/* ─── Envelope Container (dissolves away) ─────────── */}
          <motion.div
            className={`relative z-20 ${contained ? '' : 'w-full max-w-[420px]'}`}
            style={contained ? { width: envelopeWidth } : undefined}
            animate={isOpened ? {
              opacity: 0,
              scale: 1.03,
              y: 20,
              pointerEvents: 'none',
            } : {
              opacity: 1,
              scale: pressed ? 0.98 : 1,   // Tap press feedback
              y: 0,
              pointerEvents: 'auto',
            }}
            transition={isOpened ? {
              delay: M.fade.delay,
              duration: M.fade.duration,
              ease: M.ease,
            } : {
              duration: 0.15,
              ease: 'easeOut',
            }}
          >
            {/* 3:4 aspect ratio with perspective — maxHeight 88vh keeps it "object-like" */}
            {/* Z-depth illusion: envelope tilts slightly then rests into place */}
            <motion.div
              className="relative w-full cursor-pointer"
              style={{
                aspectRatio: '3 / 4',
                maxHeight: contained ? undefined : '88vh',
                perspective: '1800px',
              }}
              initial={{ rotateX: 6, scale: 0.96 }}
              animate={{ rotateX: 0, scale: 1 }}
              transition={{ duration: 0.8, ease: M.ease }}
              onClick={isClosed ? handleOpen : undefined}
              onPointerDown={isClosed ? handlePressStart : undefined}
              onPointerUp={handlePressEnd}
              onPointerLeave={handlePressEnd}
              data-testid="envelope-container"
            >
              {/* Ground shadow — physical grounding */}
              <div className="absolute inset-x-6 -bottom-[18px] h-[40px] rounded-full blur-2xl pointer-events-none"
                style={{ background: 'rgba(0,0,0,0.2)' }}
              />

              <EnvelopeBase theme={theme} />

              <InnerCard
                theme={theme}
                isOpening={isOpening}
                isOpened={isOpened}
                coupleNames={coupleNames}
                eventDate={eventDate}
                accent={c.accent}
              />

              <EnvelopeFlap
                theme={theme}
                isOpening={isOpening}
                isOpened={isOpened}
              />

              <Seal
                theme={theme}
                isOpening={isOpening}
                isOpened={isOpened}
                initials={initials}
                contained={contained}
              />

              <AnimatePresence>
                {isClosed && <OpenHint onOpen={handleOpen} isDark={isDark} />}
              </AnimatePresence>

              {/* Global lighting — simulates overhead light source */}
              <motion.div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'radial-gradient(ellipse at top, rgba(255,255,255,0.6), rgba(255,255,255,0.05) 60%, rgba(0,0,0,0.15))',
                  borderRadius: GEO.envelopeRadius,
                }}
                initial={false}
                animate={open ? { opacity: 0.5 } : { opacity: 1 }}
                transition={{ duration: 0.8 }}
              />
            </motion.div>
          </motion.div>

          {/* ─── Full-screen tap target when closed ───────────── */}
          {isClosed && !contained && (
            <button
              type="button"
              aria-label="Open invitation"
              className="absolute inset-0 z-30"
              onClick={handleOpen}
              style={{ background: 'transparent', border: 'none' }}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export { ENVELOPE_THEMES };
